import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Key, Loader2, Save, CheckCircle2, XCircle, ExternalLink, Settings2, Eye, EyeOff, Play,
} from 'lucide-react';

// Iter58 — req importado de services/api.js
import { req } from '../services/api';

const CAT_ORDER = ['IA', 'Contratación Pública', 'Email', 'Complementaria', 'Otros'];

export default function ConfiguracionAPIs() {
  const [servicios, setServicios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edits, setEdits] = useState({});   // { nombre: {api_key?: string, activo?: bool} }
  const [saving, setSaving] = useState({});
  const [testing, setTesting] = useState({});
  const [results, setResults] = useState({});
  const [visibles, setVisibles] = useState({});

  const cargar = async () => {
    setLoading(true);
    try {
      const list = await req('/api/admin/api-config');
      setServicios(list);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { cargar(); }, []);

  const handleKeyChange = (nombre, value) =>
    setEdits((e) => ({ ...e, [nombre]: { ...(e[nombre] || {}), api_key: value } }));

  const guardar = async (nombre) => {
    const payload = edits[nombre];
    if (!payload || (!('api_key' in payload) && !('activo' in payload))) return;
    setSaving((s) => ({ ...s, [nombre]: true }));
    try {
      await req(`/api/admin/api-config/${nombre}`, { method: 'PATCH', body: JSON.stringify(payload) });
      toast.success('Servicio actualizado');
      setEdits((e) => ({ ...e, [nombre]: {} }));
      cargar();
    } catch (err) {
      toast.error(err.message);
    } finally { setSaving((s) => ({ ...s, [nombre]: false })); }
  };

  const toggleActivo = async (s, nuevo) => {
    setSaving((x) => ({ ...x, [s.nombre]: true }));
    try {
      await req(`/api/admin/api-config/${s.nombre}`, {
        method: 'PATCH',
        body: JSON.stringify({ activo: nuevo }),
      });
      toast.success(`${s.label} ${nuevo ? 'activado' : 'desactivado'}`);
      cargar();
    } catch (err) { toast.error(err.message); }
    finally { setSaving((x) => ({ ...x, [s.nombre]: false })); }
  };

  const testear = async (nombre) => {
    setTesting((t) => ({ ...t, [nombre]: true }));
    try {
      const r = await req(`/api/admin/api-config/${nombre}/test`, { method: 'POST' });
      setResults((x) => ({ ...x, [nombre]: r }));
    } catch (err) {
      setResults((x) => ({ ...x, [nombre]: { ok: false, mensaje: err.message } }));
    } finally { setTesting((t) => ({ ...t, [nombre]: false })); }
  };

  const grouped = servicios.reduce((acc, s) => {
    (acc[s.categoria] = acc[s.categoria] || []).push(s);
    return acc;
  }, {});

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="config-apis-page">
      <div>
        <h1 className="text-3xl font-bold text-ink-900 font-display tracking-tight flex items-center gap-2">
          <Settings2 className="w-7 h-7 text-brand-500" />
          Configuración de APIs
        </h1>
        <p className="text-sm text-ink-500 mt-1">
          Gestiona las claves de los servicios externos. Las claves se guardan cifradas (AES-128 Fernet) en la base de datos.
          Los módulos del CRM consultan esta tabla en tiempo real; al guardar una clave los servicios pasan a usarla inmediatamente.
        </p>
      </div>

      {loading ? (
        <Card className="p-10 text-center">
          <Loader2 className="w-5 h-5 animate-spin inline" />
        </Card>
      ) : (
        CAT_ORDER.filter((c) => grouped[c]).map((cat) => (
          <div key={cat} className="space-y-3">
            <h2 className="text-lg font-semibold text-ink-700">{cat}</h2>
            {grouped[cat].map((s) => {
              const edit = edits[s.nombre] || {};
              const visible = visibles[s.nombre];
              const result = results[s.nombre];
              return (
                <Card key={s.id} className="p-4" data-testid={`api-card-${s.nombre}`}>
                  <div className="flex flex-col md:flex-row md:items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-ink-900">{s.label}</span>
                        {s.activo
                          ? <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-xs">Activo</Badge>
                          : <Badge className="bg-ink-100 text-ink-500 border-ink-200 text-xs">Desactivado</Badge>}
                        {s.placeholder && (
                          <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-xs">placeholder</Badge>
                        )}
                        {s.requiere_key && !s.tiene_key && s.activo && (
                          <Badge className="bg-rose-100 text-rose-700 border-rose-200 text-xs">sin clave</Badge>
                        )}
                      </div>
                      <p className="text-xs text-ink-500 mt-1">{s.descripcion}</p>
                      {s.url_docs && (
                        <a href={s.url_docs} target="_blank" rel="noreferrer"
                           className="text-xs text-brand-600 inline-flex items-center gap-1 mt-1 hover:underline">
                          <ExternalLink className="w-3 h-3" /> Documentación
                        </a>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      <span className="text-xs text-ink-500">Activo</span>
                      <Switch
                        checked={s.activo}
                        disabled={saving[s.nombre]}
                        onCheckedChange={(v) => toggleActivo(s, v)}
                        data-testid={`toggle-${s.nombre}`}
                      />
                    </div>
                  </div>

                  {s.requiere_key && (
                    <div className="mt-4 grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-2 items-start">
                      <div className="relative">
                        <Key className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-400 pointer-events-none" />
                        <Input
                          type={visible ? 'text' : 'password'}
                          placeholder={s.tiene_key ? '••••••••••••  (clave guardada — deja vacío para mantener)' : 'Introduce la API key'}
                          value={edit.api_key ?? ''}
                          onChange={(e) => handleKeyChange(s.nombre, e.target.value)}
                          className="pl-9 pr-10 font-mono text-xs"
                          data-testid={`input-key-${s.nombre}`}
                        />
                        <button
                          type="button"
                          onClick={() => setVisibles((v) => ({ ...v, [s.nombre]: !v[s.nombre] }))}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-400 hover:text-ink-700"
                          tabIndex={-1}
                        >
                          {visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      <Button
                        onClick={() => guardar(s.nombre)}
                        disabled={saving[s.nombre] || edit.api_key == null}
                        data-testid={`btn-save-${s.nombre}`}
                      >
                        {saving[s.nombre] ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                        {edit.api_key === '' ? 'Borrar clave' : 'Guardar'}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => testear(s.nombre)}
                        disabled={testing[s.nombre] || !s.activo}
                        data-testid={`btn-test-${s.nombre}`}
                      >
                        {testing[s.nombre] ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
                        Probar
                      </Button>
                    </div>
                  )}

                  {!s.requiere_key && (
                    <div className="mt-4">
                      <Button
                        variant="outline"
                        onClick={() => testear(s.nombre)}
                        disabled={testing[s.nombre] || !s.activo}
                        size="sm"
                        data-testid={`btn-test-${s.nombre}`}
                      >
                        {testing[s.nombre] ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
                        Probar conexión
                      </Button>
                    </div>
                  )}

                  {result && (
                    <div className={`mt-3 text-xs flex items-center gap-2 px-3 py-2 rounded-md ${result.ok ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
                      {result.ok ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                      {result.mensaje}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        ))
      )}
    </div>
  );
}
